/**
 * ============================================================
 * Fur-Real — Cinematic Splash Screen  v3.5  (Sound Feature)
 * FILE: assets/fur-real-splash.js
 * LOAD IN: layout/theme.liquid (before </body>, defer)
 *
 * External dependency: GSAP (loaded via CDN in theme.liquid)
 *
 * v3.5 changes vs v3.4 — Opt-in Sound
 * ─────────────────────────────────────────────────────
 * Adds optional, user-initiated sound. Browsers block autoplay
 * audio with sound, so nothing plays until the user explicitly
 * grants permission via a real click/tap. Two ways to grant it:
 *
 *   1. Speaker icon (top-right) — explicit, always-visible
 *      mute/unmute toggle. Starts muted. Click enables sound
 *      and (if the dog hasn't reached the bowl yet) immediately
 *      starts the sniff loop.
 *
 *   2. First-click-anywhere on the splash — a convenience
 *      shortcut. If sound is currently off, the very first
 *      click anywhere inside the splash (skip button, logo,
 *      bowl, background, etc.) also enables sound, same as
 *      clicking the speaker icon. This listener only ENABLES
 *      sound — it never mutes — so it can't accidentally
 *      undo a sound preference the user already set via the
 *      speaker icon. Once sound is on, this listener is inert.
 *
 * Audio behavior:
 *   - Sniffing loops continuously once enabled, and stops the
 *     instant reveal begins (normal completion OR Skip).
 *   - Sniffing and barking never overlap — sniff is always
 *     paused/reset before bark starts.
 *   - Bark plays once, untrimmed, only if sound is enabled.
 *   - All audio is stopped on full website activation so
 *     nothing lingers after the splash is gone.
 *
 * Also fixes a source of the "NotSupportedError" seen during
 * development: Audio() objects are now only constructed when a
 * real asset URL is present (a missing/undefined URL previously
 * produced a literal "undefined" source, which browsers reject).
 * Failed loads/playback are caught silently — no console errors.
 *
 * No visual, timing, or animation changes to any existing
 * element. All Phase 1-5 behavior (entry gate, session flag,
 * asset availability check, Mode 1A hold, Mode 1B early exit +
 * background load, Mode 2 expedited path, paw canvas, bowl
 * sequence, logo flight, GSAP reveal, CSS fallback) is
 * unchanged.
 * ============================================================
 */

(function FurRealSplash() {
  'use strict';

  /* ── ENTRY DETECTION ────────────────────────────────────────── */

  const SESSION_KEY      = 'furreal_session_entered';
  const ASSETS_READY_KEY = 'furreal_assets_ready';

  function hasEnteredThisSession() {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; }
    catch (e) { return false; }
  }

  function markSessionEntered() {
    try { sessionStorage.setItem(SESSION_KEY, '1'); }
    catch (e) {}
  }

  function markAssetsReady() {
    try { localStorage.setItem(ASSETS_READY_KEY, '1'); }
    catch (e) {}
  }

  function isInternalReferrer() {
    try {
      const ref = document.referrer;
      if (!ref) return false;
      return new URL(ref).hostname === window.location.hostname;
    } catch (e) { return false; }
  }

  /* ── CONFIGURATION ──────────────────────────────────────────── */
  const CONFIG = {

    factPools: {
      opener: [
        "Retrievers were bred to fetch hunted birds from freezing lakes.",
        "Dogs can smell direction, not just scent.",
        "Pointers freeze mid-step because hunters bred them to point at prey.",
        "Some dogs can detect storms before humans notice them.",
        "Bloodhounds can follow trails that are several days old.",
        "Dogs can hear frequencies humans can't.",
        "A Greyhound can outrun most horses in short bursts.",
        "Some dogs remember scents for years.",
        "Newfoundland dogs were bred to rescue people from water.",
        "Dogs use wet noses to trap scent particles."
      ],
      atmospheric: [
        "Dogs circle before sleeping because wild dogs flattened grass this way.",
        "Greyhounds were bred for sight, not smell.",
        "Border Collies guide sheep using eye contact.",
        "Huskies can slow their metabolism during extreme cold.",
        "Dogs instinctively protect sleeping pack members.",
        "Some dogs naturally prefer one human over everyone else.",
        "A dog's yawn can spread to humans emotionally.",
        "Many dogs learn routines faster than commands.",
        "Dogs often memorize paths after one walk.",
        "Ancient dogs slept close to fires with humans."
      ],
      psychological: [
        "Dogs tilt their heads to better read human expressions.",
        "Some dogs can recognize humans by scent alone.",
        "Puppies dream more frequently than adult dogs.",
        "Dogs can associate emotions with tone of voice.",
        "Dogs can miss specific humans for years.",
        "Many dogs recognize unfair treatment instantly.",
        "Dogs often mirror their owner's stress levels.",
        "Eye contact releases oxytocin in both dogs and humans.",
        "Dogs read body language faster than spoken words.",
        "Some dogs understand over 100 human words."
      ],
      ending: [
        "Preparing your experience...",
        "Getting everything ready...",
        "Almost ready...",
        "One moment while we prepare things...",
        "Setting things up fur-real..."
      ]
    },

    quoteInterval: 2800,

    criticalAssets: (() => {
      const assets   = [];
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        for (let i = 1; i <= 244; i++) {
          assets.push(
            `https://cdn.shopify.com/s/files/1/0703/1318/1218/files/Mobile_frame_${String(i).padStart(3, '0')}.png`
          );
        }
      } else {
        for (let i = 1; i <= 244; i++) {
          assets.push(
            `https://cdn.shopify.com/s/files/1/0703/1318/1218/files/desktopFrame_${String(i).padStart(4, '0')}.png`
          );
        }
      }
      return assets;
    })(),

    fonts: [],
    hardTimeout: 120000,
    revealThreshold: 0.9,
  };

  /* ── FACT SEQUENCE ──────────────────────────────────────────── */
  function getRandomFromPool(pool) {
    return pool[Math.floor(Math.random() * pool.length)];
  }
  function buildFactSequence() {
    return [
      getRandomFromPool(CONFIG.factPools.opener),
      getRandomFromPool(CONFIG.factPools.atmospheric),
      getRandomFromPool(CONFIG.factPools.psychological),
      getRandomFromPool(CONFIG.factPools.ending)
    ];
  }
  const SESSION_FACTS = buildFactSequence();

  /* ── DOM REFS ───────────────────────────────────────────────── */
  const el = {
    splash:      document.getElementById('fr-splash'),
    center:      document.getElementById('fr-center'),
    logoWrap:    document.getElementById('fr-logo-wrap'),
    logo:        document.getElementById('fr-logo'),
    tagline:     document.getElementById('fr-tagline'),
    quotesWrap:  document.getElementById('fr-quotes'),
    bowl:        document.getElementById('fr-bowl'),
    bowlFilled:  document.getElementById('fr-bowl-filled'),
    bowlEmpty:   document.getElementById('fr-bowl-empty'),
    bowlGlow:    document.getElementById('fr-bowl-glow'),
    canvas:      document.getElementById('fr-paw-canvas'),
    skip:        document.getElementById('fr-skip'),
    soundToggle: null,
    navbarLogo:  null,
  };

  /* ── GUARD ──────────────────────────────────────────────────── */
  if (!el.splash) {
    window.__furRealSplashDone = true;
    document.dispatchEvent(new CustomEvent('furreal:splashComplete', {
      bubbles: true, detail: { timestamp: Date.now() },
    }));
    return;
  }

  /* ── ENTRY GATE ─────────────────────────────────────────────────────
   * Initial visibility is handled pre-paint by the inline <script> in
   * fur-real-splash.liquid, which runs synchronously before the browser
   * draws anything:
   *   In-session → splash stays at opacity:0 (fr-splash--entering in HTML)
   *   External   → fr-splash--entering removed → splash at opacity:1
   *
   * The deferred JS must NOT re-add fr-splash--entering here. Doing so
   * would briefly hide the splash on external entry (between the inline
   * script removing it and this deferred script running), causing exactly
   * the "page visible before splash" bug we are solving.
   * ─────────────────────────────────────────────────────────────────── */

  function suppressSplash() {
    el.splash.classList.add('fr-splash--done');
    document.documentElement.classList.remove('fr-locked');
    document.body.classList.remove('fr-blurred', 'fr-breathing');
    const navLogoEl = document.getElementById('fr-navbar-logo');
    if (navLogoEl) { navLogoEl.style.opacity = ''; navLogoEl.style.transition = ''; }
    window.__furRealSplashDone = true;
    document.dispatchEvent(new CustomEvent('furreal:splashComplete', {
      bubbles: true, detail: { timestamp: Date.now(), suppressed: true },
    }));
  }

  if (hasEnteredThisSession()) { suppressSplash(); return; }
  if (isInternalReferrer())    { markSessionEntered(); suppressSplash(); return; }

  /* ── EXTERNAL ENTRY CONFIRMED ── MODE SELECTION ──────────────── */

  const MODES = {
    EXPEDITED: 'mode2',
    FULL_HOLD: 'mode1a',
    FAST_EXIT: 'mode1b',
  };

  /* Mode 1B thresholds */
  const MODE_1B_EXIT_THRESHOLD  = 0.25;
  const MODE_1B_MIN_DURATION_MS = 2800;

  /**
   * Mode 2 progression duration.
   * 320 ms synthetic paw-trail fill. Combined with 2× timeScale
   * on the GSAP reveal (~1.15 s at 2×), total Mode 2 experience
   * is approximately 1.4–1.5 s.
   */
  const MODE_2_PROGRESSION_MS = 320;

  /**
   * GSAP timeline speed multiplier per mode.
   * Mode 2 uses 2.0 — same timeline, same visuals, 2× speed.
   * Modes 1A and 1B use 1.0 — identical to all prior behavior.
   */
  const MODE_2_TIMESCALE = 2.0;

  function isHomepage() {
    const path = window.location.pathname;
    return path === '/' || path === '';
  }

  function areAssetsReadilyAvailable() {
    try { return localStorage.getItem(ASSETS_READY_KEY) === '1'; }
    catch (e) { return false; }
  }

  function selectMode() {
    if (areAssetsReadilyAvailable()) return MODES.EXPEDITED;
    return isHomepage() ? MODES.FULL_HOLD : MODES.FAST_EXIT;
  }

  const selectedMode = selectMode();
  window.__furRealMode = selectedMode;

  /* ── STATE ──────────────────────────────────────────────────── */
  let realProgress      = 0;   // actual loading fraction (0-1)
  let visualProgress    = 0;   // smoothly animated value driving the paw canvas
  let revealAllowedAt   = 0;   // don't reveal before this timestamp (Mode 1B)
  let progressRafActive = false;

  let revealed             = false;
  let quoteTimer           = null;
  let currentQuote         = 0;
  let hardTimer            = null;
  let startTime            = Date.now();
  let exitThreshold        = CONFIG.revealThreshold;
  let effectiveMinDuration = 0;
  let backgroundComplete   = false;

  /**
   * Timeline speed multiplier used by revealWithGSAP() and
   * revealWithCSS(). Set by runMode2(). Default 1.0 (unchanged
   * behavior for all other modes).
   */
  let revealTimeScale = 1.0;

  /**
   * Sound state. Starts muted/off — no audio plays until the user
   * explicitly grants permission via a real click (speaker icon or
   * first click anywhere on the splash). This satisfies browser
   * autoplay-with-sound policy, which requires a user gesture.
   */
  let soundEnabled = false;

  /* ── MODE RUNNERS ───────────────────────────────────────────── */

  /** Mode 1A — Full cinematic, homepage, hold until 100% ready. */
  function runMode1A() {
    exitThreshold = 1.0;
    preloadCriticalAssets();
    startProgressAnimation();
  }

  /** Mode 1B — Fast cinematic, non-homepage, early exit + background load. */
  function runMode1B() {
    exitThreshold        = MODE_1B_EXIT_THRESHOLD;
    effectiveMinDuration = MODE_1B_MIN_DURATION_MS;
    preloadCriticalAssets();
    startProgressAnimation();
  }

  /**
   * Mode 2 — Expedited, assets available, fixed progression.
   *
   * Initialises the frame URL list so embroidery finds it after
   * activation. Does NOT start image loading — embroidery retrieves
   * frames from the https cache when it activates.
   *
   * Runs a 320 ms RAF ticker that drives updatePawProgress() with
   * synthetic easeOutQuad values (0→1). On tick completion, calls
   * triggerReveal() directly — bypassing the loading-system path
   * (setProgress / onLoadComplete) which is irrelevant here.
   *
   * revealTimeScale = 2.0 causes the GSAP timeline to run at 2×
   * speed: same bowl sequence, same logo flight, same easings —
   * just twice as fast.
   */
  function runMode2() {
    // Provide frame URL list for embroidery without loading frames.
    window.__furRealFrameUrls = CONFIG.criticalAssets.slice();
    if (!window.__furRealFrameCache) {
      window.__furRealFrameCache = Object.create(null);
    }

    revealTimeScale = MODE_2_TIMESCALE;

    const start = Date.now();

    function tick() {
      const elapsed = Date.now() - start;
      const t       = Math.min(elapsed / MODE_2_PROGRESSION_MS, 1);
      const eased   = 1 - Math.pow(1 - t, 2); // easeOutQuad — confident fill

      updatePawProgress(eased);

      if (t < 1) {
        requestAnimationFrame(tick);
      } else {
        // Synthetic progression complete. Draw all paws, then reveal.
        updatePawProgress(1);
        triggerReveal();
      }
    }

    requestAnimationFrame(tick);
  }

  /* ── INIT ───────────────────────────────────────────────────── */
  function init() {
    lockScroll();
    applyBodyBlur();
    loadFonts();

    requestAnimationFrame(() => {
      el.splash.classList.remove('fr-splash--entering');
      el.splash.classList.add('fr-splash--visible');
    });

    startQuotes();

    // Load core splash assets (paw icons + sniff/bark audio) first.
    // Only once they're ready — or CORE_ASSET_TIMEOUT_MS passes, so a
    // slow connection can't stall things indefinitely — do we start the
    // 244-image frame preload batch. This keeps the small audio/icon
    // files from being starved of bandwidth/priority behind the much
    // larger frame request flood in Modes 1A/1B.
    const coreAssetsPromise = initPawCanvas();
    createSoundToggle();
    initFirstClickSoundEnable();

    coreAssetsPromise.then(() => {
      if      (selectedMode === MODES.EXPEDITED)  { runMode2();  }
      else if (selectedMode === MODES.FULL_HOLD)  { runMode1A(); }
      else                                        { runMode1B(); }
    });

    setTimeout(() => { if (!revealed) el.skip.style.display = 'block'; }, 5000);
    hardTimer = setTimeout(() => { if (!revealed) triggerReveal(); }, CONFIG.hardTimeout);
    el.skip.addEventListener('click', () => { if (!revealed) triggerReveal(); });
  }

  /* ── SCROLL LOCK ────────────────────────────────────────────── */
  function lockScroll()   { document.documentElement.classList.add('fr-locked'); }
  function unlockScroll() { document.documentElement.classList.remove('fr-locked'); }

  /* ── BODY BLUR & BREATHING ──────────────────────────────────── */
  function applyBodyBlur()  { document.body.classList.add('fr-blurred', 'fr-breathing'); }
  function stopBreathing()  { document.body.classList.remove('fr-breathing'); }
  function removeBodyBlur() { document.body.classList.remove('fr-blurred'); }

  /* ── FONT LOADING ───────────────────────────────────────────── */
  function loadFonts() {
    CONFIG.fonts.forEach(({ url }) => {
      const link = document.createElement('link');
      link.rel  = 'stylesheet';
      link.href = url;
      document.head.appendChild(link);
    });
  }

  /* ── QUOTE ROTATION ─────────────────────────────────────────── */
  function startQuotes() {
    const quoteEls = el.quotesWrap.querySelectorAll('.fr-splash__quote');
    if (!quoteEls.length) return;
    quoteEls.forEach((q, i) => { if (SESSION_FACTS[i]) q.textContent = SESSION_FACTS[i]; });
    if (quoteEls[0]) quoteEls[0].classList.add('fr-splash__quote--active');
    quoteTimer = setInterval(() => {
      const items = el.quotesWrap.querySelectorAll('.fr-splash__quote');
      items[currentQuote].classList.remove('fr-splash__quote--active');
      currentQuote = (currentQuote + 1) % items.length;
      items[currentQuote].classList.add('fr-splash__quote--active');
    }, CONFIG.quoteInterval);
  }
  function stopQuotes() { clearInterval(quoteTimer); }

  /* ── ASSET PRELOADING (Modes 1A and 1B only) ─────────────────── */
  function preloadCriticalAssets() {
    if (!window.__furRealFrameCache) window.__furRealFrameCache = Object.create(null);
    const cache = window.__furRealFrameCache;
    window.__furRealFrameUrls = CONFIG.criticalAssets.slice();

    const assets = CONFIG.criticalAssets.filter(Boolean);
    const total  = assets.length + 1; // +1 for fonts unit
    let   loaded = 0;

    function onUnit() { loaded++; setProgress(loaded / total); }

    document.fonts.ready.then(onUnit).catch(onUnit);

    if (assets.length === 0) { simulateFallbackProgress(); return; }

    assets.forEach(src => {
      const img = new Image();
      img.onload  = () => { img._ok = true;  cache[src] = img; onUnit(); };
      img.onerror = () => { img._ok = false; cache[src] = img; onUnit(); };
      img.src = src;
      if (img.complete && !cache[src]) { img._ok = true; cache[src] = img; }
    });
  }

  function simulateFallbackProgress() {
    const start  = Date.now();
    const target = CONFIG.revealThreshold - 0.08;
    function tick() {
      const t     = Math.min((Date.now() - start) / 1, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setProgress(eased * target);
      if (t < 1) { requestAnimationFrame(tick); } else { setTimeout(onLoadComplete, 200); }
    }
    document.fonts.ready.then(() => requestAnimationFrame(tick));
  }

  /* ── PROGRESS SYSTEM (Modes 1A and 1B only) ─────────────────── */

  /**
   * Updates the real (network) loading fraction.
   * Visual updates are handled by startProgressAnimation() — this
   * function does NOT call updatePawProgress() directly. Decoupling
   * real from visual progress enables the Chrome-style smooth animation.
   */
  function setProgress(value) {
    realProgress = Math.max(realProgress, Math.min(value, 1));
    if (realProgress >= exitThreshold && !loadComplete) onLoadComplete();
    if (realProgress >= 1.0 && selectedMode === MODES.FAST_EXIT) onBackgroundComplete();
  }

  let loadComplete = false;

  /**
   * Sets completion flags. The progress animation RAF calls triggerReveal()
   * once visualProgress reaches 0.995 and revealAllowedAt has passed.
   */
  function onLoadComplete() {
    if (loadComplete) return;
    loadComplete = true;
    if (selectedMode === MODES.FULL_HOLD) markAssetsReady();
    const elapsed = Date.now() - startTime;
    revealAllowedAt = Date.now() + Math.max(0, effectiveMinDuration - elapsed);
  }

  function onBackgroundComplete() {
    if (backgroundComplete) return;
    backgroundComplete = true;
    markAssetsReady();
    document.dispatchEvent(new CustomEvent('furreal:assetsComplete', {
      bubbles: true, detail: { timestamp: Date.now(), mode: selectedMode },
    }));
  }

  /**
   * Chrome-style smooth progress animation.
   *
   * Runs a dedicated RAF loop that drives visualProgress toward
   * realProgress using velocity-based easing with three soft walls.
   *
   * INSTANT_START — 2 paws appear on the first frame. Users immediately
   *   register the trail as a progress indicator.
   * CRAWL — visualProgress advances autonomously even with zero real
   *   loading. Paws keep moving so the screen never looks frozen.
   * Soft walls at 30%, 62%, 85% — progress slows as real loading lags,
   *   mirroring Chrome's bar stalling near 70-90%.
   * Fast finish — when loadComplete fires, lerp rate jumps and paws
   *   close quickly to the bowl before the reveal fires.
   * revealAllowedAt — Mode 1B minimum duration gate: even if paws reach
   *   99.5%, reveal waits until the visitor has seen the first quote.
   */
  function startProgressAnimation() {
    if (progressRafActive) return;
    progressRafActive = true;

    const INSTANT_START = 0.16;    // jump to ~2 paws on first frame
    const CRAWL_RATE    = 0.000018; // per ms — reaches WALL_3 in ~47s autonomously
    const WALL_1 = 0.30, LERP_1 = 0.12;
    const WALL_2 = 0.62, LERP_2 = 0.04;
    const WALL_3 = 0.85, LERP_3 = 0.010;
    const LERP_FINAL = 0.18;

    let lastTime        = Date.now();
    let revealTriggered = false;

    function tick() {
      const now = Date.now();
      const dt  = Math.min(now - lastTime, 50);
      lastTime  = now;

      const realVisual  = Math.min(realProgress / exitThreshold, 1.0);
      const crawlCeil   = loadComplete ? 1.0 : WALL_3;
      const crawlTarget = Math.min(visualProgress + CRAWL_RATE * dt, crawlCeil);
      const target      = loadComplete
        ? 1.0
        : Math.max(realVisual, INSTANT_START, crawlTarget);

      let lerpRate;
      if (loadComplete)                  lerpRate = LERP_FINAL;
      else if (visualProgress >= WALL_2) lerpRate = LERP_3;
      else if (visualProgress >= WALL_1) lerpRate = LERP_2;
      else                               lerpRate = LERP_1;

      visualProgress = Math.min(
        visualProgress + (target - visualProgress) * lerpRate,
        target
      );

      updatePawProgress(visualProgress);

      if (loadComplete && visualProgress >= 0.995 && now >= revealAllowedAt) {
        if (!revealTriggered) {
          revealTriggered   = true;
          progressRafActive = false;
          updatePawProgress(1.0);
          triggerReveal();
        }
        return;
      }

      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }

  /* ── PAW CANVAS SYSTEM ──────────────────────────────────────── */
  const PAW_PATHS = {
  desktop: [
{ x: 0.88, y: 0.94 },
  { x: 0.84, y: 0.88 },
  { x: 0.80, y: 0.82 },
  { x: 0.76, y: 0.75 },
  { x: 0.72, y: 0.68 },

  // Begin the large downward curve
  { x: 0.68, y: 0.61 },
  { x: 0.64, y: 0.56 },
  { x: 0.60, y: 0.53 },
  { x: 0.56, y: 0.52 },
  { x: 0.52, y: 0.53 },
  { x: 0.48, y: 0.56 },
  { x: 0.44, y: 0.60 },
  { x: 0.40, y: 0.64 },

  // Bottom of the U (well below the logo)
  { x: 0.36, y: 0.68 },
  { x: 0.32, y: 0.69 },
  { x: 0.28, y: 0.68 },

  // Climb back up
  { x: 0.25, y: 0.63 },
  { x: 0.22, y: 0.57 },
  { x: 0.20, y: 0.50 },
  { x: 0.18, y: 0.43 },
  { x: 0.17, y: 0.36 },
  // { x: 0.17, y: 0.29 },
  // { x: 0.18, y: 0.22 },
  // { x: 0.20, y: 0.16 }
  ],

mobile: [
  { x: 0.89, y: 0.93 },
  { x: 0.86, y: 0.88 },
  { x: 0.83, y: 0.83 },
  { x: 0.80, y: 0.78 },
  { x: 0.77, y: 0.73 },
  { x: 0.74, y: 0.68 },

  // Begin curving to the right
  { x: 0.72, y: 0.62 },
  { x: 0.71, y: 0.56 },
  { x: 0.72, y: 0.50 },
  { x: 0.74, y: 0.44 },

  // Curve around the logo
  { x: 0.70, y: 0.38 },
  { x: 0.64, y: 0.33 },
  { x: 0.57, y: 0.28 },
  { x: 0.50, y: 0.24 },
  { x: 0.43, y: 0.21 },
  { x: 0.36, y: 0.19 }
]
};

const selectedPath =
  window.innerWidth <= 768
    ? PAW_PATHS.mobile
    : PAW_PATHS.desktop;
  let pawImgL = null,
    pawImgR = null,
    canvasReady = false,
    ctx = null,

    sniffAudio = null,
    barkAudio = null;

  /**
   * Safety cap on how long we wait for core splash assets (paw icons +
   * audio) before starting the 244-image frame preload batch anyway.
   * Prevents a slow connection from indefinitely delaying the frames.
   */
  const CORE_ASSET_TIMEOUT_MS = 2500;

  function waitForAudioReady(audioEl) {
    return new Promise(resolve => {
      let settled = false;
      function done() { if (!settled) { settled = true; resolve(); } }
      audioEl.addEventListener('canplaythrough', done, { once: true });
      audioEl.addEventListener('error', done, { once: true });
      setTimeout(done, CORE_ASSET_TIMEOUT_MS);
    });
  }

  /**
   * Sets up the paw canvas plus the sniff/bark Audio elements, and
   * returns a Promise that resolves once the paw icons are loaded and
   * the audio elements are ready to play through (or
   * CORE_ASSET_TIMEOUT_MS elapses). init() waits on this before
   * starting the 244-image frame preload batch, so those large
   * requests don't starve these small core assets of bandwidth/priority
   * (this was causing the sniff sound to only start right before reveal).
   */
  function initPawCanvas() {
    ctx = el.canvas.getContext('2d');

    const splashAssets = window.__furRealSplashAssets || {};
    const readyPromises = [];

    // Guard: only construct Audio() when a real URL is present. Passing
    // undefined creates a source of the literal string "undefined",
    // which browsers reject with NotSupportedError.
    if (splashAssets.sniff) {
      sniffAudio = new Audio(splashAssets.sniff);
      sniffAudio.preload = 'auto';
      sniffAudio.loop = true;
      sniffAudio.volume = 0.99;
      sniffAudio.addEventListener('error', () => {
        console.error('[FurReal Sound] sniff failed to load:', sniffAudio.error);
      });
      readyPromises.push(waitForAudioReady(sniffAudio));
    }

    if (splashAssets.bark) {
      barkAudio = new Audio(splashAssets.bark);
      barkAudio.preload = 'auto';
      barkAudio.volume  = 0.99;
      barkAudio.addEventListener('error', () => {
        console.error('[FurReal Sound] bark failed to load:', barkAudio.error);
      });
      readyPromises.push(waitForAudioReady(barkAudio));
    }

    // No autoplay here — sound only starts once the user grants
    // permission via the speaker toggle or a first click on the
    // splash (see createSoundToggle / initFirstClickSoundEnable).

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const pawReadyPromise = new Promise(resolve => {
      let loaded = 0;
      function onPawLoaded() {
        loaded++;
        if (loaded >= 2 && !canvasReady) { canvasReady = true; updatePawProgress(visualProgress); resolve(); }
      }
      pawImgL = new Image(); pawImgR = new Image();
      pawImgL.onload  = () => { pawImgL._ok = true;  onPawLoaded(); };
      pawImgL.onerror = () => { pawImgL._ok = false; onPawLoaded(); };
      pawImgR.onload  = () => { pawImgR._ok = true;  onPawLoaded(); };
      pawImgR.onerror = () => { pawImgR._ok = false; onPawLoaded(); };

      pawImgL.src = splashAssets.pawLeft  || '';
      pawImgR.src = splashAssets.pawRight || '';
      if (pawImgL.complete) { pawImgL._ok = true; onPawLoaded(); }
      if (pawImgR.complete) { pawImgR._ok = true; onPawLoaded(); }
    });
    readyPromises.push(pawReadyPromise);

    return Promise.all(readyPromises);
  }

  function resizeCanvas() {
    el.canvas.width  = window.innerWidth;
    el.canvas.height = window.innerHeight;
    if (canvasReady) updatePawProgress(visualProgress);
  }

  function updatePawProgress(p) {
    if (!canvasReady || !ctx) return;
    const W = el.canvas.width, H = el.canvas.height;
    const path = selectedPath, total = path.length;
    ctx.clearRect(0, 0, W, H);
    const isMobile  = W <= 768;

const PAW_CONFIG = isMobile
  ? {
      size: 0.039,
      lateral: 0.90
    }
  : {
      size: 0.038,
      lateral: 0.90
    };
const size = Math.min(W, H) * PAW_CONFIG.size;
const lateral = size * PAW_CONFIG.lateral;

    const pawFilter = 'brightness(0) invert(1) sepia(0.55) saturate(4) hue-rotate(15deg) brightness(1.25)';
    let lastPx = -1, lastPy = -1; // track leading paw position for glow
    let activePaw = null;
    for (let i = 0; i < total; i++) {
      if (i >= p * total) break;
const pt = path[i];
const cx = pt.x * W;
const cy = pt.y * H;
const prev = path[Math.max(0, i - 1)];
const next = path[Math.min(total - 1, i + 1)];
      const travelAngle = Math.atan2((next.y - prev.y) * H, (next.x - prev.x) * W);
      const perpX = Math.cos(travelAngle - Math.PI / 2);
      const perpY = Math.sin(travelAngle - Math.PI / 2);
      const side  = (i % 2 === 0) ? -1 : 1;
      ctx.save();
      ctx.globalAlpha = 0.76;
      ctx.filter      = pawFilter;
      ctx.translate(cx + perpX * lateral * side, cy + perpY * lateral * side);
      ctx.rotate(travelAngle + Math.PI / 2);
      const img = (i % 2 === 0) ? pawImgL : pawImgR;

const pawX = cx + perpX * lateral * side;
const pawY = cy + perpY * lateral * side;

if (i === Math.floor(p * total) - 1) {

  activePaw = {
    img,
    x: pawX,
    y: pawY,
    angle: travelAngle
  };

} else {

  if (img && img._ok) {
    ctx.drawImage(img, -size / 2, -size / 2, size, size);
  }

}

ctx.restore();

lastPx = pawX;
lastPy = pawY;
    }
    ctx.filter = 'none';

    /* Leading paw glow — pulses on the active front paw.
     * Communicates 'still working' even when progress appears stalled.
     * Fades out when paws reach the bowl (p >= 0.99).
     */
    if (lastPx >= 0 && p > 0.01 && p < 0.99) {
      const pulse      = 0.5 + 0.5 * Math.sin(Date.now() / 380);
      const glowRadius = size * 2.6;
      const grad = ctx.createRadialGradient(lastPx, lastPy, 0, lastPx, lastPy, glowRadius);
      grad.addColorStop(0,   `rgba(201,169,110,${(0.60 * pulse).toFixed(2)})`);
      grad.addColorStop(0.4, `rgba(201,169,110,${(0.18 * pulse).toFixed(2)})`);
      grad.addColorStop(1,   'rgba(201,169,110,0)');
      ctx.save();
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(lastPx, lastPy, glowRadius, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
    /* Draw the active paw ON TOP of the glow */
if (activePaw && activePaw.img && activePaw.img._ok) {

    ctx.save();

    ctx.globalAlpha = 0.76;
    ctx.filter = pawFilter;

    ctx.translate(activePaw.x, activePaw.y);
    ctx.rotate(activePaw.angle + Math.PI / 2);

    ctx.drawImage(
        activePaw.img,
        -size / 2,
        -size / 2,
        size,
        size
    );

    ctx.restore();
}
  }

  /* ── SOUND TOGGLE ───────────────────────────────────────────── */

  const SOUND_ICON_MUTED = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9v6h4l5 4V5L8 9H4z"/><line x1="16.5" y1="9.5" x2="21.5" y2="14.5"/><line x1="21.5" y1="9.5" x2="16.5" y2="14.5"/></svg>';
  const SOUND_ICON_ON     = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M4 9v6h4l5 4V5L8 9H4z"/><path d="M16.5 8.5a5 5 0 0 1 0 7"/><path d="M19 6a8.5 8.5 0 0 1 0 12"/></svg>';

  function createSoundToggle() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.id = 'fr-sound-toggle';
    btn.className = 'fr-splash__sound fr-splash__sound--muted';
    btn.setAttribute('aria-label', 'Enable sound');
    btn.innerHTML = SOUND_ICON_MUTED;
    // stopPropagation so this explicit toggle click isn't also caught by
    // the first-click-anywhere listener on el.splash (harmless either way
    // since that listener only enables, but this keeps intent explicit).
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onSoundToggleClick();
    });
    el.splash.appendChild(btn);
    el.soundToggle = btn;
  }

  /**
   * Explicit mute/unmute toggle (speaker icon). Always flips state,
   * in either direction.
   */
  function onSoundToggleClick() {
    if (soundEnabled) {
      disableSound();
    } else {
      enableSound();
    }
  }

  /**
   * Convenience shortcut: the first click ANYWHERE on the splash (while
   * sound is still off) also enables sound, same as the speaker icon.
   * This listener only ever enables — it never mutes — so it can't
   * undo a preference the user already set. It becomes inert once
   * sound is on, and is removed after firing once.
   */
  function initFirstClickSoundEnable() {
    function onFirstClick() {
      if (!soundEnabled) enableSound();
      el.splash.removeEventListener('click', onFirstClick);
    }
    el.splash.addEventListener('click', onFirstClick);
  }

  /**
   * Turns sound on and reflects that in the toggle icon. Called from a
   * real user click (speaker icon or first-click-anywhere), so calling
   * .play() here satisfies browser autoplay-with-sound policy.
   */
  function enableSound() {
    soundEnabled = true;
    if (el.soundToggle) {
      el.soundToggle.classList.remove('fr-splash__sound--muted');
      el.soundToggle.classList.add('fr-splash__sound--on');
      el.soundToggle.innerHTML = SOUND_ICON_ON;
      el.soundToggle.setAttribute('aria-label', 'Mute sound');
    }
    // Only start sniffing if the dog hasn't reached the bowl yet.
    if (!revealed && sniffAudio) {
      sniffAudio.play()
        .then(() => console.log('[FurReal Sound] sniff playing'))
        .catch(err => console.error('[FurReal Sound] sniff play() failed:', err.name, err.message));
    }
  }

  /** Turns sound off and reflects that in the toggle icon. */
  function disableSound() {
    soundEnabled = false;
    if (el.soundToggle) {
      el.soundToggle.classList.remove('fr-splash__sound--on');
      el.soundToggle.classList.add('fr-splash__sound--muted');
      el.soundToggle.innerHTML = SOUND_ICON_MUTED;
      el.soundToggle.setAttribute('aria-label', 'Enable sound');
    }
    if (sniffAudio) sniffAudio.pause();
  }

  /* ── REVEAL SEQUENCE ────────────────────────────────────────── */

  /**
   * Triggers the reveal animation sequence.
   * revealTimeScale is applied by revealWithGSAP() and revealWithCSS().
   * For Mode 2: revealTimeScale = 2.0. For all other modes: 1.0.
   */
  function triggerReveal() {

    if (revealed) return;

    revealed = true;

    // Stop sniffing immediately — must never overlap with the bark, and
    // must stop whether reveal came from normal progression or Skip.
    if (sniffAudio) {
      sniffAudio.pause();
      sniffAudio.currentTime = 0;
    }

    // Bark only plays if the user opted into sound.
    if (soundEnabled && barkAudio) {
      barkAudio.currentTime = 0;
      const p = barkAudio.play();
      if (p) {
        p.then(() => console.log('[FurReal Sound] bark playing'))
         .catch(err => console.error('[FurReal Sound] bark play() failed:', err.name, err.message));
      }
    }

    clearTimeout(hardTimer);
    stopQuotes();
    stopBreathing();
    if (window.gsap) { revealWithGSAP(); } else { revealWithCSS(); }
  }

  /* ── GSAP REVEAL ────────────────────────────────────────────── */

  /**
   * Identical timeline to all previous phases.
   * tl.timeScale(revealTimeScale) is the only Mode 2 difference:
   *   - Mode 2: 2.0 — same animations, 2× wall-clock speed (~1.15 s)
   *   - Mode 1A / 1B: 1.0 — unchanged behavior
   */
  function revealWithGSAP() {
    const gsap = window.gsap;
    const tl   = gsap.timeline({ onComplete: activateWebsite });

    tl.timeScale(revealTimeScale);

    const D = {
      bowlPulse: 0.14, bowlEat: 0.45, bowlGlow: 0.62, beat: 0.85,
      tagline:   0.60, quotes:  0.50, canvas:   0.55, overlay: 0.90, flight: 0.92,
    };

    tl.addLabel('eat', 0);
    gsap.set(el.bowlFilled, { opacity: 1, scale: 1, transformOrigin: 'center center' });
    gsap.set(el.bowlEmpty,  { opacity: 0, scale: 1.3, transformOrigin: 'center center' });
    tl.to(el.bowlFilled, { scale: 1.3, duration: 0.40, ease: 'power2.out' }, 'eat');
    tl.call(() => {
      el.bowlFilled.style.opacity = '0';
      el.bowlEmpty.style.opacity  = '1';
    }, null, 'eat+=0.18');
    tl.to(el.bowlEmpty, { scale: 0, duration: 0.80, ease: 'power2.inOut' }, 'eat+=0.18');
    tl.to(el.bowlGlow, {
      scale: 2.8, opacity: 0, duration: D.bowlGlow,
      ease: 'power3.out', transformOrigin: 'center center',
    }, `eat+=${D.bowlPulse + 0.12}`);

    const beatEnd = D.bowlPulse + D.bowlEat + D.beat;
    tl.addLabel('reveal', `eat+=${beatEnd}`);
    tl.to(el.bowl,       { opacity: 0, duration: 0.45, ease: 'power2.inOut' }, 'reveal');
    tl.to(el.tagline,    { opacity: 0, y: -10, duration: D.tagline, ease: 'power2.inOut' }, 'reveal');
    tl.to(el.quotesWrap, { opacity: 0, duration: D.quotes, ease: 'power2.inOut' }, 'reveal');
    tl.to(el.canvas,     { opacity: 0, duration: D.canvas, ease: 'power2.inOut' }, 'reveal');
    animateLogoToNavbar(tl, gsap, 'reveal', D.flight);
    tl.to(el.splash, {
      opacity: 0, duration: D.overlay, ease: 'power2.inOut',
      onComplete: () => { removeBodyBlur(); el.splash.classList.add('fr-splash--done'); unlockScroll(); },
    }, 'reveal+=0.15');
  }

  /* ── LOGO FLIGHT ────────────────────────────────────────────── */
  function animateLogoToNavbar(tl, gsap, label, flightDur) {
    const logoEl   = el.logo;
    const navLogo  = document.getElementById('fr-navbar-logo');
    const fromRect = logoEl.getBoundingClientRect();
    const toRect   = navLogo ? navLogo.getBoundingClientRect() : null;
    const isValid  = (r) => r && r.width > 0 && r.height > 0;

    if (!navLogo || !isValid(toRect) || !isValid(fromRect)) {
      tl.to(logoEl, { scale: 0.55, opacity: 0, duration: 0.72, ease: 'power3.inOut' }, `${label}+=0.05`);
      if (navLogo) tl.set(navLogo, { opacity: 1 }, `${label}+=0.70`);
      return;
    }

    const scaleTo = Math.min(toRect.width / fromRect.width, toRect.height / fromRect.height);
    const dx = toRect.left - fromRect.left, dy = toRect.top - fromRect.top;
    const arcLift = -Math.min(Math.abs(dy) * 0.16, 55);
    const fStart  = `${label}+=0.06`;
    const fPivot  = `${label}+=${0.06 + flightDur * 0.38}`;
    const fFade   = `${label}+=${0.06 + flightDur * 0.72}`;
    const fadeDur = 0.26;

    const clone = logoEl.cloneNode(true);
    clone.id = ''; clone.classList.add('fr-logo-clone');
    clone.style.cssText = `width:${fromRect.width}px;height:${fromRect.height}px;top:${fromRect.top}px;left:${fromRect.left}px;`;
    document.body.appendChild(clone);
    tl.set(logoEl, { opacity: 0 }, `${label}+=0.04`);
    navLogo.style.opacity = '0'; navLogo.style.transition = 'none';

    tl.to(clone, { x: dx, duration: flightDur, ease: 'power3.inOut' }, fStart);
    tl.to(clone, { y: arcLift, duration: flightDur * 0.38, ease: 'power2.out' }, fStart);
    tl.to(clone, { y: dy, duration: flightDur * 0.62, ease: 'power3.in' }, fPivot);
    tl.to(clone, { scale: scaleTo, transformOrigin: 'top left', duration: flightDur, ease: 'power3.inOut' }, fStart);
    tl.to(clone, { opacity: 0, duration: fadeDur, ease: 'power2.inOut', onComplete: () => clone.remove() }, fFade);
    tl.to(navLogo, { opacity: 1, duration: fadeDur, ease: 'power2.inOut' }, fFade);
  }

  /* ── CSS-ONLY FALLBACK ──────────────────────────────────────── */

  /**
   * CSS fallback applies revealTimeScale by dividing setTimeout
   * durations. For Mode 2 (timeScale=2): all waits are halved.
   * For other modes (timeScale=1): unchanged behavior.
   */
  function revealWithCSS() {
    const ts = revealTimeScale;
    if (el.bowlFilled) { el.bowlFilled.style.transition = `opacity ${0.35/ts}s ease, transform ${0.35/ts}s ease`; el.bowlFilled.style.opacity = '0'; el.bowlFilled.style.transform = 'scale(0.80)'; }
    if (el.bowlEmpty)  { el.bowlEmpty.style.transition  = `opacity ${0.35/ts}s ease ${0.15/ts}s`; el.bowlEmpty.style.opacity = '1'; }
    setTimeout(() => {
      if (el.tagline)    { el.tagline.style.transition    = `opacity ${0.55/ts}s ease, transform ${0.55/ts}s ease`; el.tagline.style.opacity = '0'; el.tagline.style.transform = 'translateY(-10px)'; }
      if (el.quotesWrap) { el.quotesWrap.style.transition = `opacity ${0.45/ts}s ease`; el.quotesWrap.style.opacity = '0'; }
      if (el.logo)       { el.logo.style.transition       = `opacity ${0.5/ts}s ease, transform ${0.5/ts}s ease`; el.logo.style.opacity = '0'; el.logo.style.transform = 'scale(0.5)'; }
      if (el.bowl)       { el.bowl.style.transition       = `opacity ${0.45/ts}s ease`; el.bowl.style.opacity = '0'; }
      const navLogo = document.getElementById('fr-navbar-logo');
      if (navLogo) { navLogo.style.transition = `opacity ${0.5/ts}s ease ${0.3/ts}s`; navLogo.style.opacity = '1'; }
      setTimeout(() => {
        el.splash.classList.add('fr-splash--exiting');
        setTimeout(() => { removeBodyBlur(); el.splash.classList.add('fr-splash--done'); unlockScroll(); activateWebsite(); }, 950 / ts);
      }, 200 / ts);
    }, 480 / ts);
  }

  /* ── WEBSITE ACTIVATION ─────────────────────────────────────── */
  function activateWebsite() {
    markSessionEntered();
    // Ensure no audio lingers once the splash is gone.
    if (sniffAudio) sniffAudio.pause();
    if (barkAudio)  barkAudio.pause();
    const navLogo = document.getElementById('fr-navbar-logo');
    if (navLogo) { navLogo.style.opacity = ''; navLogo.style.transition = ''; }
    document.dispatchEvent(new CustomEvent('furreal:splashComplete', {
      bubbles: true, detail: { timestamp: Date.now(), mode: selectedMode },
    }));
    window.__furRealSplashDone = true;
  }

  /* ── BOOT ───────────────────────────────────────────────────── */
  // Splash visibility was set pre-paint by the inline script in fur-real-splash.liquid.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}());